import { useCallback, useRef, useState } from "react";

import type { ScoutTurnStatus } from "../hooks/types";

const IN_FLIGHT_TURN_STATUSES = new Set<ScoutTurnStatus>(["starting", "streaming"]);
const TERMINAL_TURN_STATUSES = new Set<ScoutTurnStatus>(["finished", "error", "aborted"]);

/**
 * Latches an unseen-response state on the minimized Scout launcher when a turn
 * finishes while the popover is minimized. Does not latch if the user minimizes
 * after the response is already done — only an in-flight → terminal transition
 * while minimized counts.
 */
export function useScoutMinimizedCompletionBadge(
  isMinimized: boolean,
  activeTurnStatus: ScoutTurnStatus | undefined
): { showCompletionBadge: boolean; clearCompletionBadge: () => void } {
  const previousStatusRef = useRef<ScoutTurnStatus | undefined>(activeTurnStatus);
  const badgeLatchedRef = useRef(false);
  const [, forceRender] = useState(0);

  const previousStatus = previousStatusRef.current;

  if (!isMinimized) {
    badgeLatchedRef.current = false;
  } else if (
    previousStatus &&
    IN_FLIGHT_TURN_STATUSES.has(previousStatus) &&
    activeTurnStatus &&
    TERMINAL_TURN_STATUSES.has(activeTurnStatus)
  ) {
    badgeLatchedRef.current = true;
  }

  previousStatusRef.current = activeTurnStatus;

  const clearCompletionBadge = useCallback(() => {
    badgeLatchedRef.current = false;
    forceRender((version) => version + 1);
  }, []);

  return {
    showCompletionBadge: isMinimized && badgeLatchedRef.current,
    clearCompletionBadge,
  };
}
